import React, { useState, useEffect, useRef } from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Search, Filter, RefreshCw, Calendar, User, Package, MapPin, AlertCircle, X, MessageSquare } from 'lucide-react';
import { supabase } from '../lib/supabase';
import useAppStore from '../store';
import { LogEntry } from '../types';

const LogsPage: React.FC = () => {
  const { currentUser } = useAppStore();
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState<string>('');
  const [actionFilter, setActionFilter] = useState<string>('');
  const [entityFilter, setEntityFilter] = useState<string>('');
  const [userFilter, setUserFilter] = useState<string>('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [activeTooltip, setActiveTooltip] = useState<string | null>(null);
  const [smsCount, setSmsCount] = useState<{ total: number; sinceReset: number } | null>(null);
  const [isSmsCountLoading, setIsSmsCountLoading] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const logsPerPage = 10; // Changed to 10 results per page

  // Close tooltip when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target as Node)) {
        setActiveTooltip(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Check if user is admin
  useEffect(() => {
    if (currentUser?.role !== 'admin') {
      setError('Vous n\'avez pas les droits nécessaires pour accéder à cette page.');
      setLoading(false);
      return;
    }

    fetchLogs();
    fetchSmsCount();
  }, [currentUser, page, dateFilter, actionFilter, entityFilter, userFilter]);

  const fetchSmsCount = async () => {
    try {
      setIsSmsCountLoading(true);
      const { data, error } = await supabase.rpc('get_sms_count');
      
      if (error) throw error;
      
      setSmsCount({
        total: data.total || 0,
        sinceReset: data.since_reset || 0
      });
    } catch (error) {
      console.error('Error fetching SMS count:', error);
    } finally {
      setIsSmsCountLoading(false);
    }
  };

  const resetSmsCounter = async () => {
    try {
      const { data, error } = await supabase.rpc('reset_sms_counter');
      
      if (error) throw error;
      
      if (data.success) {
        await fetchSmsCount();
      } else {
        console.error('Failed to reset SMS counter:', data.error);
      }
    } catch (error) {
      console.error('Error resetting SMS counter:', error);
    }
  };

  const fetchLogs = async () => {
    try {
      setLoading(true);
      setError(null);

      // Build the query
      let query = supabase
        .from('logs')
        .select(`
          *,
          user:users!logs_user_id_fkey (
            first_name,
            last_name,
            email
          )
        `, { count: 'exact' })
        .order('created_at', { ascending: false })
        .not('action', 'eq', 'debug_user_sync') // Filter out debug entries
        .range((page - 1) * logsPerPage, page * logsPerPage - 1);

      // Apply filters
      if (dateFilter) {
        const startDate = new Date(dateFilter);
        const endDate = new Date(dateFilter);
        endDate.setDate(endDate.getDate() + 1);
        
        query = query
          .gte('created_at', startDate.toISOString())
          .lt('created_at', endDate.toISOString());
      }

      if (actionFilter) {
        query = query.eq('action', actionFilter);
      }

      if (entityFilter) {
        query = query.eq('entity_type', entityFilter);
      }

      if (userFilter) {
        query = query.eq('user_id', userFilter);
      }

      if (searchTerm) {
        query = query.or(`user_id.ilike.%${searchTerm}%,entity_id.ilike.%${searchTerm}%`);
      }

      // Execute the query
      const { data, error, count } = await query;

      if (error) throw error;

      // Format the logs
      const formattedLogs = data.map(log => ({
        id: log.id,
        userId: log.user_id,
        action: log.action,
        entityType: log.entity_type,
        entityId: log.entity_id,
        details: log.details,
        createdAt: log.created_at,
        user: log.user ? {
          firstName: log.user.first_name,
          lastName: log.user.last_name,
          email: log.user.email
        } : undefined
      }));

      setLogs(formattedLogs);
      
      if (count !== null) {
        setTotalPages(Math.ceil(count / logsPerPage));
      }
    } catch (error) {
      console.error('Error fetching logs:', error);
      setError('Une erreur est survenue lors de la récupération des logs.');
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    fetchLogs();
    fetchSmsCount();
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1); // Reset to first page when searching
    fetchLogs();
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setDateFilter('');
    setActionFilter('');
    setEntityFilter('');
    setUserFilter('');
    setPage(1);
  };

  const getActionLabel = (action: string) => {
    switch (action) {
      case 'user_login': return 'Connexion';
      case 'user_logout': return 'Déconnexion';
      case 'user_created': return 'Utilisateur créé';
      case 'user_updated': return 'Utilisateur modifié';
      case 'user_deleted': return 'Utilisateur supprimé';
      case 'task_created': return 'Tâche créée';
      case 'task_started': return 'Tâche démarrée';
      case 'task_completed': return 'Tâche terminée';
      case 'task_cancelled': return 'Tâche annulée';
      case 'task_updated': return 'Tâche modifiée';
      case 'location_created': return 'Point GPS créé';
      case 'location_updated': return 'Point GPS modifié';
      case 'location_deleted': return 'Point GPS supprimé';
      default: return action;
    }
  };

  const getEntityLabel = (entityType: string) => {
    switch (entityType) {
      case 'users': return 'Utilisateur';
      case 'tasks': return 'Tâche';
      case 'client_locations': return 'Point GPS';
      case 'clients': return 'Client';
      case 'drivers': return 'Chauffeur';
      case 'dumpsters': return 'Benne';
      case 'depots': return 'Dépôt';
      default: return entityType;
    }
  };

  const getActionIcon = (action: string) => {
    if (action.startsWith('user_')) {
      return <User className="h-4 w-4" />;
    } else if (action.startsWith('task_')) {
      return <Package className="h-4 w-4" />;
    } else if (action.startsWith('location_')) {
      return <MapPin className="h-4 w-4" />;
    } else {
      return <AlertCircle className="h-4 w-4" />;
    }
  };

  const getActionColor = (action: string) => {
    if (action.includes('created') || action.includes('login')) {
      return 'bg-green-100 text-green-800';
    } else if (action.includes('updated') || action.includes('started')) {
      return 'bg-blue-100 text-blue-800';
    } else if (action.includes('deleted') || action.includes('cancelled') || action.includes('logout')) {
      return 'bg-red-100 text-red-800';
    } else if (action.includes('completed')) {
      return 'bg-purple-100 text-purple-800';
    } else {
      return 'bg-gray-100 text-gray-800';
    }
  };

  // If user is not admin, show access denied message
  if (currentUser?.role !== 'admin') {
    return (
      <div className="bg-white rounded-lg shadow p-6 text-center">
        <h2 className="text-xl font-bold mb-2">Accès refusé</h2>
        <p>Vous n'avez pas les droits nécessaires pour accéder à cette page.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <h2 className="text-xl font-bold">Logs système</h2>

          <div className="flex items-center space-x-2">
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className={`bg-gray-100 text-gray-800 px-4 py-2 rounded-lg flex items-center justify-center hover:bg-gray-200 ${
                isRefreshing ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
              {isRefreshing ? 'Actualisation...' : 'Actualiser'}
            </button>
          </div>
        </div>
      </div>

      {/* SMS Counter */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4">
          <h2 className="text-lg font-semibold">Compteur SMS</h2>
          <button
            onClick={resetSmsCounter}
            className="mt-2 md:mt-0 px-3 py-1 bg-blue-100 text-blue-800 rounded-lg hover:bg-blue-200 text-sm"
          >
            Réinitialiser
          </button>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="text-sm text-blue-600 mb-1">Total</div>
            <div className="text-2xl font-bold text-blue-800">
              {isSmsCountLoading ? (
                <div className="animate-pulse h-8 w-16 bg-blue-200 rounded"></div>
              ) : (
                smsCount?.total.toLocaleString() || '0'
              )}
            </div>
          </div>
          
          <div className="bg-green-50 p-4 rounded-lg">
            <div className="text-sm text-green-600 mb-1">Depuis réinitialisation</div>
            <div className="text-2xl font-bold text-green-800">
              {isSmsCountLoading ? (
                <div className="animate-pulse h-8 w-16 bg-green-200 rounded"></div>
              ) : (
                smsCount?.sinceReset.toLocaleString() || '0'
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <form onSubmit={handleSearch} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                <input
                  type="date"
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Action
              </label>
              <select
                value={actionFilter}
                onChange={(e) => setActionFilter(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Toutes les actions</option>
                <option value="user_login">Connexion</option>
                <option value="user_logout">Déconnexion</option>
                <option value="user_created">Création d'utilisateur</option>
                <option value="user_updated">Modification d'utilisateur</option>
                <option value="task_created">Création de tâche</option>
                <option value="task_started">Démarrage de tâche</option>
                <option value="task_completed">Fin de tâche</option>
                <option value="task_cancelled">Annulation de tâche</option>
                <option value="location_created">Création de point GPS</option>
                <option value="location_updated">Modification de point GPS</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Entité
              </label>
              <select
                value={entityFilter}
                onChange={(e) => setEntityFilter(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Toutes les entités</option>
                <option value="users">Utilisateurs</option>
                <option value="tasks">Tâches</option>
                <option value="client_locations">Points GPS</option>
                <option value="clients">Clients</option>
                <option value="drivers">Chauffeurs</option>
                <option value="dumpsters">Bennes</option>
                <option value="depots">Dépôts</option>
              </select>
            </div>

            <div className="flex items-end space-x-2">
              <button
                type="submit"
                className="px-4 py-2 bg-blue-800 text-white rounded-lg hover:bg-blue-700 flex items-center"
              >
                <Filter className="h-4 w-4 mr-2" />
                Filtrer
              </button>
              
              <button
                type="button"
                onClick={handleClearFilters}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Réinitialiser
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Logs table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {error ? (
          <div className="p-4 text-red-600">{error}</div>
        ) : loading ? (
          <div className="p-4 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-800"></div>
            <p className="mt-2 text-gray-600">Chargement des logs...</p>
          </div>
        ) : logs.length === 0 ? (
          <div className="p-4 text-center text-gray-600">
            Aucun log trouvé pour les critères sélectionnés.
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Utilisateur
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Action
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Entité
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Détails
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {logs.map((log) => (
                    <tr key={log.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {format(new Date(log.createdAt), 'dd/MM/yyyy HH:mm:ss', { locale: fr })}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {log.user ? (
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center">
                              <span className="text-blue-800 font-medium">
                                {log.user.firstName[0]}{log.user.lastName[0]}
                              </span>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {log.user.firstName} {log.user.lastName}
                              </div>
                              <div className="text-sm text-gray-500">
                                {log.user.email}
                              </div>
                            </div>
                          </div>
                        ) : (
                          <span className="text-gray-500">Système</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getActionColor(log.action)}`}>
                          {getActionIcon(log.action)}
                          <span className="ml-1">{getActionLabel(log.action)}</span>
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {getEntityLabel(log.entityType)}
                        {log.entityId && (
                          <span className="text-xs text-gray-500 ml-1">
                            ({log.entityId.substring(0, 8)}...)
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 relative">
                        {log.details && (
                          <>
                            <button
                              onClick={() => setActiveTooltip(activeTooltip === log.id ? null : log.id)}
                              className="text-blue-600 hover:text-blue-800 underline"
                            >
                              Voir détails
                            </button>
                            
                            {activeTooltip === log.id && (
                              <div 
                                ref={tooltipRef}
                                className="absolute z-10 right-0 mt-2 w-96 bg-white rounded-lg shadow-lg border border-gray-200 p-4"
                              >
                                <div className="flex justify-between items-center mb-2">
                                  <h3 className="font-medium">Détails</h3>
                                  <button 
                                    onClick={() => setActiveTooltip(null)}
                                    className="text-gray-500 hover:text-gray-700"
                                  >
                                    <X className="h-4 w-4" />
                                  </button>
                                </div>
                                <pre className="whitespace-pre-wrap font-sans text-xs bg-gray-50 p-2 rounded max-h-60 overflow-y-auto">
                                  {JSON.stringify(log.details, null, 2)}
                                </pre>
                              </div>
                            )}
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-6 py-4 flex items-center justify-between border-t border-gray-200">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                    className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                      page === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Précédent
                  </button>
                  <button
                    onClick={() => setPage(Math.min(totalPages, page + 1))}
                    disabled={page === totalPages}
                    className={`ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                      page === totalPages ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Suivant
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Affichage de la page <span className="font-medium">{page}</span> sur <span className="font-medium">{totalPages}</span>
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                      <button
                        onClick={() => setPage(Math.max(1, page - 1))}
                        disabled={page === 1}
                        className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 text-sm font-medium ${
                          page === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        <span className="sr-only">Précédent</span>
                        &laquo;
                      </button>
                      
                      {/* Page numbers */}
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNumber;
                        if (totalPages <= 5) {
                          pageNumber = i + 1;
                        } else if (page <= 3) {
                          pageNumber = i + 1;
                        } else if (page >= totalPages - 2) {
                          pageNumber = totalPages - 4 + i;
                        } else {
                          pageNumber = page - 2 + i;
                        }
                        
                        return (
                          <button
                            key={pageNumber}
                            onClick={() => setPage(pageNumber)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                              page === pageNumber
                                ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                            }`}
                          >
                            {pageNumber}
                          </button>
                        );
                      })}
                      
                      <button
                        onClick={() => setPage(Math.min(totalPages, page + 1))}
                        disabled={page === totalPages}
                        className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 text-sm font-medium ${
                          page === totalPages ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        <span className="sr-only">Suivant</span>
                        &raquo;
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default LogsPage;